import { Component, OnInit, Input, Output, ChangeDetectionStrategy, EventEmitter, OnDestroy } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { IDateOption } from '../models/date-option.interface';
import {Subject} from "rxjs";
import { takeUntil } from "rxjs/operators"
import { FilterFactory } from '../models/filter-factory.interface';
import { IFilter } from '../models/filter.interface';
import * as moment from "moment";
import { SearchDateValidator } from './date.validator';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchComponent implements OnInit, OnDestroy {
  @Input() dateOptions: IDateOption[] = [];
  @Input() filterFactories: FilterFactory[] = [];
  @Input() searchAfterInit = true;
  @Input() hideFilter = false;
  @Output() queryBuilt = new EventEmitter<string>();
  @Output() isFormValid = new EventEmitter<boolean>();

  isFilterCreatorEnabled = false;
  isDateRangeEnabled = false;
  rangeDateOption?: IDateOption;

  private _destroySubject: Subject<void> = new Subject();

  searchForm = new FormGroup({
    date: new FormControl('', [SearchDateValidator]),
    filters: new FormArray([])
  });

  rangeForm = new FormGroup({
    start: new FormControl(''),
    end: new FormControl('')
  });

  rangeStart = new Date();
  rangeEnd = new Date();

  get filters(): FormArray {
    return this.searchForm.get("filters") as FormArray;
  }

  constructor() {}

  ngOnInit(): void {
    this.searchForm.get("date")?.valueChanges.pipe(takeUntil(this._destroySubject)).subscribe(option => {

      const date: IDateOption = option;
      this.isDateRangeEnabled = !!date.range;
      if (this.isDateRangeEnabled) {
        this.rangeDateOption = date;
      }
    });

    this.searchForm.statusChanges.pipe(takeUntil(this._destroySubject)).subscribe(status => {
      if (!this.searchForm.valid) {
        this.isFormValid.emit(false);
        return;
      }

      let queries: string[] = [];
      const { date, filters } = this.searchForm.value;

      queries.push((date as IDateOption).query);
      queries = [
        ...queries,
        ...(filters as Array<IFilter>).map(filter => filter.query)
      ];

      this.queryBuilt.emit(
        queries.join(" and ")
      );

      this.isFormValid.emit(true);
    });

    this.searchForm.patchValue({
      date: this.dateOptions[0]
    });
  }

  ngOnDestroy(): void {
    this._destroySubject.next();
  }

  onStartDateChange(event: MatDatepickerInputEvent<any>): void {
    this.rangeDateOption!.start = moment(event.value);
  }

  onEndDateChange(event: MatDatepickerInputEvent<any>): void {
    this.rangeDateOption!.end = moment(event.value);
  }

  onCustomDateFilterSelected(): void {
    this.searchForm.patchValue({
      date: this.rangeDateOption!
    });
  }

  enableFilterCreator(): void {
    this.isFilterCreatorEnabled = true;
  }

  disableFilterCreator(): void {
    this.isFilterCreatorEnabled = false;
  }

  filterCreated(filter: IFilter): void {
    const { field, operator, criteria, query } = filter;
    this.filters.push(new FormGroup({
      field: new FormControl(field),
      operator: new FormControl(operator),
      criteria: new FormControl(criteria),
      query: new FormControl(query)
    }));
  }

  removeFilter(filterIndex: number): void {
    this.filters.removeAt(filterIndex);
  }
}
